import React from "react";

function isHorizontalRule(line) {
  return /^(\*{3,}|-{3,}|_{3,})\s*$/.test(line.trim());
}

function isTableRow(line) {
  const trimmed = line.trim();
  return trimmed.startsWith("|") && trimmed.includes("|", 1);
}

function isTableSeparator(line) {
  const trimmed = line.trim();
  if (!trimmed.includes("-")) return false;
  return /^\|?(\s*:?-{3,}:?\s*\|)+\s*$/.test(trimmed);
}

function parseTableCells(line) {
  return line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map(cell => cell.trim());
}

/**
 * Lightweight markdown renderer for in-app help (headings, lists, tables, hr, code, mermaid).
 * @param {string} content
 * @param {{ renderInline: (text: string) => React.ReactNode, keyPrefix?: string }} options
 */
export function renderHelpMarkdown(content, { renderInline, keyPrefix = "help" }) {
  const lines = content.split("\n");
  const blocks = [];
  let listItems = [];
  let listOrdered = false;
  let codeFence = null;
  let tableRows = [];
  let blockIndex = 0;

  const nextKey = (suffix) => `${keyPrefix}-${blockIndex++}-${suffix}`;

  const flushList = () => {
    if (listItems.length === 0) return;
    const ListTag = listOrdered ? "ol" : "ul";
    blocks.push(
      <ListTag key={nextKey("list")} className={listOrdered ? "help-ol" : "help-ul"}>
        {listItems.map((item, index) => (
          <li key={index}>{renderInline(item)}</li>
        ))}
      </ListTag>
    );
    listItems = [];
    listOrdered = false;
  };

  const flushTable = () => {
    if (tableRows.length === 0) return;

    const separatorIndex = tableRows.findIndex(isTableSeparator);
    const hasHeader = separatorIndex === 1;
    const headerRow = hasHeader ? parseTableCells(tableRows[0]) : null;
    const bodyRows = hasHeader
      ? tableRows.slice(2)
      : separatorIndex === 0
        ? tableRows.slice(1)
        : tableRows;

    if (!headerRow || bodyRows.length === 0) {
      tableRows.forEach((row, index) => {
        blocks.push(<p key={nextKey(`table-fallback-${index}`)}>{renderInline(row.trim())}</p>);
      });
      tableRows = [];
      return;
    }

    blocks.push(
      <div key={nextKey("table-wrap")} className="help-table-wrap">
        <table className="help-table">
          <thead>
            <tr>
              {headerRow.map((cell, index) => (
                <th key={index}>{renderInline(cell)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bodyRows.filter(row => !isTableSeparator(row)).map((row, rowIndex) => {
              const cells = parseTableCells(row);
              return (
                <tr key={rowIndex}>
                  {cells.map((cell, cellIndex) => (
                    <td key={cellIndex}>{renderInline(cell)}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
    tableRows = [];
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    if (trimmed.indexOf("```") === 0) {
      flushList();
      flushTable();
      if (codeFence) {
        const code = codeFence.lines.join("\n");
        if (codeFence.language === "mermaid") {
          blocks.push(
            <div key={nextKey("mermaid")} className="mermaid">
              {code}
            </div>
          );
        } else {
          blocks.push(
            <pre key={nextKey("code")} className="help-code-block">
              <code>{code}</code>
            </pre>
          );
        }
        codeFence = null;
      } else {
        codeFence = {
          language: trimmed.replace(/```/g, "").trim().toLowerCase(),
          lines: [],
        };
      }
      return;
    }

    if (codeFence) {
      codeFence.lines.push(line);
      return;
    }

    if (isTableRow(line)) {
      flushList();
      tableRows.push(line);
      return;
    }

    if (tableRows.length > 0) {
      flushTable();
    }

    if (!trimmed) {
      flushList();
      return;
    }

    if (isHorizontalRule(line)) {
      flushList();
      blocks.push(<hr key={nextKey("hr")} className="help-hr" />);
      return;
    }

    if (trimmed.indexOf("# ") === 0) {
      return;
    }

    if (trimmed.indexOf("## ") === 0) {
      flushList();
      blocks.push(<h3 key={nextKey("h3")}>{renderInline(trimmed.replace(/^##\s+/, ""))}</h3>);
      return;
    }

    if (trimmed.indexOf("### ") === 0) {
      flushList();
      blocks.push(<h4 key={nextKey("h4")}>{renderInline(trimmed.replace(/^###\s+/, ""))}</h4>);
      return;
    }

    if (trimmed.indexOf("#### ") === 0) {
      flushList();
      blocks.push(<h5 key={nextKey("h5")}>{renderInline(trimmed.replace(/^####\s+/, ""))}</h5>);
      return;
    }

    const orderedMatch = trimmed.match(/^\d+\.\s+(.*)$/);
    if (orderedMatch) {
      if (listItems.length > 0 && !listOrdered) flushList();
      listOrdered = true;
      listItems.push(orderedMatch[1]);
      return;
    }

    if (trimmed.indexOf("- ") === 0 || trimmed.indexOf("* ") === 0) {
      if (listItems.length > 0 && listOrdered) flushList();
      listItems.push(trimmed.replace(/^[-*]\s+/, ""));
      return;
    }

    if (trimmed.indexOf("> ") === 0) {
      flushList();
      blocks.push(
        <blockquote key={nextKey("quote")} className="help-blockquote">
          {renderInline(trimmed.replace(/^>\s+/, ""))}
        </blockquote>
      );
      return;
    }

    flushList();
    blocks.push(<p key={nextKey("p")}>{renderInline(trimmed)}</p>);
  });

  flushList();
  flushTable();

  if (codeFence) {
    const code = codeFence.lines.join("\n");
    if (codeFence.language === "mermaid") {
      blocks.push(
        <div key={nextKey("mermaid-open")} className="mermaid">
          {code}
        </div>
      );
    } else {
      blocks.push(
        <pre key={nextKey("code-open")} className="help-code-block">
          <code>{code}</code>
        </pre>
      );
    }
  }

  return blocks;
}
